import React, { useState, useEffect } from "react";
import styled from "styled-components";
import about1 from "../assets/our_mission.jpg";
import about3 from "../assets/about-3.png";
import Header from "../styles/Header";
import { motion } from "framer-motion";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../authentication/firebase";
import { FaEnvelope, FaPhone, FaLinkedin } from "react-icons/fa";

// Dummy profile images
import team1 from "../assets/team1.jpg";
import team2 from "../assets/team2.jpg";
import team3 from "../assets/team3.jpg";
import team4 from "../assets/team4.jpg";

// Reusable Section component
const SectionWidget = ({ number, title, children, backgroundColor = "#f8faf5", style = {} }) => {
  return (
    <motion.div
      className="section-widget"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      style={{ backgroundColor, ...style }}
    >
      <motion.div
        className="section-header"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <div className="section-number">{number}</div>
        <h2>{title}</h2>
      </motion.div>
      <div className="section-content">
        {children}
      </div>
    </motion.div>
  );
};

const AboutUs = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const teamCollection = collection(db, "team");
        const teamQuery = query(teamCollection, orderBy("order", "asc"));
        const teamSnapshot = await getDocs(teamQuery);
        const teamData = teamSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTeamMembers(teamData);
      } catch (err) {
        console.error("Error fetching team members:", err);
        setError("Failed to load team members");
        // Fallback to hardcoded data if Firebase fails
        setTeamMembers([
          { name: "Jane Doe", role: "Founder & CEO", photo: team1 },
          { name: "Alex Rivera", role: "Co-Founder & CTO", photo: team2 },
          { name: "Emily Johnson", role: "Environmental Scientist", photo: team3 },
          { name: "John Smith", role: "Head of Operations", photo: team4 }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);


  return (
    <>
      <Header
        title="About Us"
        description="Wrooted in action, we restore nature through hands-on projects that reforest, rebuild, and rewild the world."
      />
      <AboutWrapper>
        {/* Introduction Section */}
        <motion.div
          className="intro"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h1>Our Journey</h1>
          <div className="circle-container">
            <motion.div
              className="circle"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3>2023</h3>
              <p>Founded</p>
            </motion.div>
            <motion.div
              className="circle"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <h3>5+</h3>
              <p>Projects</p>
            </motion.div>
            <motion.div
              className="circle"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <h3>100+</h3>
              <p>Trees</p>
            </motion.div>
            <motion.div
              className="circle"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <h3>5+</h3>
              <p>Partners</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Three alternating colored section widgets with matching borders */}
        {/* Section 1: Our Mission - Light Green */}
        <SectionWidget
          number="1"
          title="Our Mission"
          backgroundColor="#f0f8f0"
          style={{ borderColor: '#b4e2b4' }} // Matching border color
        >
          <motion.div
            className="text"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="desc-container">
              <p className="desc">
                Wroot has a simple goal: to bring damaged ecosystems back to life. Though every product and initiative we offer, we fund real-world ecosystem recovery in places that need it most. We are not just planting trees. We are reconnecting people to the planet, and helping build a future “Wrooted” in resilience, regeneration, and care!
              </p>
            </div>

            <motion.div
              className="stats"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="stat">
                <h3>100+</h3>
                <p>Trees Planted</p>
              </div>
              <div className="stat">
                <h3>5+</h3>
                <p>Active Projects</p>
              </div>
              <div className="stat">
                <h3>5+</h3>
                <p>Global Partners</p>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="image-container">
              <img src={about1} alt="Our Mission" />
              <div className="image-overlay"></div>
            </div>
          </motion.div>
        </SectionWidget>

        {/* Section 2: Who We Are - Light Blue */}
        <SectionWidget
          number="2"
          title="Who We Are"
          backgroundColor="#f0f5f8"
          style={{ borderColor: '#b4cfe2' }} // Matching border color
        >
          <motion.div
            className="text full-width"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="desc-container">
              <p className="desc">
                We are a small team with a big mission. Wroot was built on a simple idea: that doing business can also mean doing good. For planet, for people, and for future generations. We’re engineers that seek to modernize current environmental recovery solutions alongside, environmentalists, storytellers, and growers. A combination of folks who care deeply about restoring what’s been lost. We work closely with on-the-ground partners and local communities to make sure every tree planted, and every project we engage creates “real lasting change”.
              </p>
            </div>

            <motion.div
              className="team-grid"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading team members...</p>
                </div>
              ) : error ? (
                <div className="error-container">
                  <p>Error loading team members. Showing fallback data.</p>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="no-data-container">
                  <p>No team members found.</p>
                </div>
              ) : (
                teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  className="team-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.6 + (index * 0.1) }}
                  whileHover={{ y: -5 }}
                >
                  <div className="photo-container">
                    <div className="photo" style={{ 
                      backgroundImage: member.photo ? `url(${member.photo})` : 'none',
                      backgroundSize: "cover", 
                      backgroundPosition: "center" 
                    }}>
                      {!member.photo && (
                        <div className="placeholder-avatar">
                          <span className="placeholder-icon">👤</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="card-content">
                    <h4>{member.name || "Team Member"}</h4>
                    <p>{member.role || "Team Member"}</p>
                    {(member.phone || member.email || member.linkedin) && (
                      <div className="social-icons">
                        {member.phone && (
                          <a href={`tel:${member.phone}`} className="icon" title="Call">
                            <FaPhone />
                          </a>
                        )}
                        {member.email && (
                          <a href={`mailto:${member.email}`} className="icon" title="Email">
                            <FaEnvelope />
                          </a>
                        )}
                        {member.linkedin && (
                          <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="icon" title="LinkedIn">
                            <FaLinkedin />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
                              ))
              )}
            </motion.div>
          </motion.div>
        </SectionWidget>
      </AboutWrapper>
    </>
  );
};

export default AboutUs;

const AboutWrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4rem;
  font-family: "Mulish", sans-serif;

  /* Introduction Section */
  .intro {
    width: var(--section-width);
    margin: var(--section-margin) auto;
    text-align: center;
    padding: 3rem 2rem;
    background-color: var(--section-bg-color);
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    border: 2px solid rgba(75, 192, 163, 0.4);
    transition: transform 0.3s ease;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    }
    
    h1 {
      font-size: 2.5rem;
      margin-bottom: 3rem;
      color: var(--text-light-color);
      position: relative;
      display: inline-block;
      
      &:after {
        content: "";
        position: absolute;
        bottom: -10px;
        left: 50%;
        transform: translateX(-50%);
        width: 80px;
        height: 4px;
        background-color: var(--primary-color);
        border-radius: 2px;
      }
    }
    
    .circle-container {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 2rem;
      
      .circle {
        width: 150px;
        height: 150px;
        border-radius: 50%;
        background-color: white;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
        
        h3 {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          color: var(--text-light-color);
        }
        
        p {
          color: var(--text-gray-color);
          font-weight: 500;
        }
      }
    }
  }

  /* Main Sections - Widget Style */
  .section-widget {
    width: var(--section-width);
    margin: 0 auto;
    padding: 3rem 2rem;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    position: relative;
    overflow: hidden;
    border: 2px solid transparent; /* Will be customized based on background color */
    transition: transform 0.3s ease;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    }
    
    .section-header {
      display: flex;
      align-items: center;
      margin-bottom: 2.5rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      padding-bottom: 1.5rem;
      
      .section-number {
        width: 60px;
        height: 60px;
        background-color: var(--primary-color);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.8rem;
        font-weight: bold;
        color: white;
        margin-right: 1.5rem;
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
      }
      
      h2 {
        font-size: 2.2rem;
        color: var(--text-light-color);
        position: relative;
      }
    }
    
    .section-content {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      justify-content: space-between;
      gap: 3rem;
      
      .text {
        flex: 1;
        min-width: 300px;

        &.full-width {
          flex: 1 1 100%;
          width: 100%;
        }

        .desc-container {
          position: relative;
          padding: 1.8rem;
          background-color: white;
          border-radius: var(--m-radius);
          margin-bottom: 2rem;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
          
          &:before {
            content: '"';
            position: absolute;
            top: -20px;
            left: 20px;
            font-size: 4rem;
            color: var(--primary-color);
            font-family: serif;
            opacity: 0.5;
          }
        }

        .desc {
          font-size: 1rem;
          color: var(--text-color);
          line-height: 1.8;
        }
        
        /* Stats Component */
        .stats {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          margin-top: 2rem;
          
          .stat {
            background-color: white;
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
            padding: 1.5rem;
            border-radius: var(--m-radius);
            min-width: 120px;
            text-align: center;
            border-top: 4px solid var(--primary-color);
            
            h3 {
              font-size: 1.8rem;
              color: var(--primary-color);
              margin-bottom: 0.5rem;
            }
            
            p {
              color: var(--text-gray-color);
              font-size: 0.9rem;
            }
          }
        }
        
        /* Team Grid */
        .team-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
          width: 100%;
          
          .loading-container, .error-container, .no-data-container {
            grid-column: 1 / -1;
            text-align: center;
            padding: 2rem;
            color: var(--text-gray-color);
            
            .loading-spinner {
              width: 40px;
              height: 40px;
              border: 4px solid #f3f3f3;
              border-top: 4px solid var(--primary-color);
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto 1rem;
            }
            
            
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          }
          
          .team-card {
            background-color: white;
            border-radius: var(--m-radius);
            overflow: hidden;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
            transition: all 0.3s ease;
            
            &:hover {
              box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
              transform: translateY(-5px);
              
              .photo-container .photo {
                transform: scale(1.05);
              }
            }
            
            .photo-container {
              height: 200px;
              overflow: hidden;
              
              .photo {
                width: 100%;
                height: 100%;
                background-size: cover;
                background-position: center;
                transition: transform 0.5s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #f8f9fa;
                
                .placeholder-avatar {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 100%;
                  height: 100%;
                  background-color: #e9ecef;
                  border-radius: 50%;
                  margin: 20px;
                  
                  .placeholder-icon {
                    font-size: 3rem;
                    color: #6c757d;
                  }
                }
              }
            }
            
            .card-content {
              padding: 1.2rem;
              text-align: center;
              
              h4 {
                color: var(--text-color);
                margin-bottom: 0.4rem;
                font-size: 1.1rem;
              }
              
              p {
                color: var(--text-gray-color);
                font-size: 0.9rem;
                margin-bottom: 1rem;
              }
              
              .social-icons {
                display: flex;
                justify-content: center;
                gap: 0.8rem;
                
                .icon {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 32px;
                  height: 32px;
                  background-color: #f8f9fa;
                  border-radius: 50%;
                  color: var(--primary-color);
                  text-decoration: none;
                  cursor: pointer;
                  transition: all 0.2s ease;
                  font-size: 14px;
                  
                  &:hover {
                    transform: scale(1.2);
                    background-color: var(--primary-color);
                    color: white;
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                  }
                }
              }
            }
          }
        }
        
        /* Benefits Grid */
        .benefits {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 1rem;
          margin-top: 2rem;
          
          .benefit {
            background-color: white;
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
            padding: 1.5rem;
            border-radius: var(--m-radius);
            text-align: center;
            transition: all 0.3s ease;
            
            &:hover {
              transform: translateY(-5px);
              box-shadow: 0 12px 25px rgba(0, 0, 0, 0.1);
            }
            
            .benefit-icon {
              font-size: 2.2rem;
              margin-bottom: 1rem;
            }
            
            p {
              color: var(--text-gray-color);
              font-size: 0.9rem;
              font-weight: 500;
            }
          }
        }
      }

      /* Image Container */
      .image {
        flex: 1;
        min-width: 300px;
        
        .image-container {
          position: relative;
          border-radius: var(--l-radius);
          overflow: hidden;
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
          
          img {
            width: 100%;
            height: auto;
            display: block;
            transform: scale(1.02);
            transition: transform 0.5s ease;
            
            &:hover {
              transform: scale(1.05);
            }
          }
          
          .image-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.2) 100%);
            pointer-events: none;
          }
        }
      }
    }
  }

  /* Responsive Styles */
  @media (max-width: 1024px) {
    .intro {
      h1 {
        font-size: 2rem;
      }
      
      .circle-container {
        .circle {
          width: 120px;
          height: 120px;
          
          h3 {
            font-size: 1.6rem;
          }
        }
      }
    }
    
    .section-widget {
      padding: 2rem 1.5rem;
      
      .section-header {
        .section-number {
          width: 50px;
          height: 50px;
          font-size: 1.5rem;
        }
        
        h2 {
          font-size: 1.8rem;
        }
      }
      
      .section-content {
        .text.full-width {
          .team-grid {
            grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          }
        }
      }
    }
  }
  
  @media (max-width: 600px) {
    gap: 3rem;
    
    .intro {
      padding: 2rem 1rem;
    }
    
    .section-widget {
      padding: 2rem 1rem;
      
      .section-header {
        flex-direction: column;
        align-items: flex-start;
        
        .section-number {
          margin-bottom: 1rem;
        }
      }
      
      .section-content {
        gap: 2rem;
        
        .text.full-width {
          .team-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }
        }
        
        .stats {
          justify-content: center;
        }
        
        .benefits {
          grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
        }
      }
    }
  }
`;